/**
 * VideoChat - Chat interface for querying video/game data
 * Uses Gemini API with full Real Madrid/Barcelona basketball context
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Send, Bot, User, Loader2, Sparkles, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { NODE_BACKEND_URL } from '../../utils/constant';
import { marked } from 'marked';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Markdown renderer (content is from our own AI backend, so it's trusted)
const renderMarkdown = (text) => {
  if (!text) return '';
  return marked.parse(text);
};

// Build dynamic system prompt from actual game data
// Replaces the former hardcoded Real Madrid/El Clásico prompt
const buildSystemPrompt = (gameData) => {
  const { gameMetadata, keyMoments, playerAnalysis, tacticalBreakdown } = gameData || {};

  const home = gameMetadata?.homeTeam || 'Home';
  const away = gameMetadata?.awayTeam || 'Away';
  const date = gameMetadata?.date || '';
  const venue = gameMetadata?.venue || '';
  const score = gameMetadata?.finalScore || '';
  const competition = gameMetadata?.competition || '';

  let prompt = `You are "The Wire" Basketball Intelligence Assistant - an elite analyst with COMPLETE ACCESS to:
- Biomechanical video analysis (six-lens: offensive, defensive, performance, tactical, biomechanics, shooting)
- Play-by-play segment analysis data
- Tactical breakdowns & coaching dossiers
- Full season context for both teams

---

## GAME CONTEXT
**${home} vs ${away}**
${date ? `**Date:** ${date}` : ''}
${venue ? `**Venue:** ${venue}` : ''}
${competition ? `**Competition:** ${competition}` : ''}
${score ? `**Final Score:** ${score}` : ''}

---
`;

  // Tactical breakdown
  if (tacticalBreakdown) {
    prompt += '\n## TACTICAL ANALYSIS\n\n';
    if (typeof tacticalBreakdown === 'string') {
      prompt += tacticalBreakdown + '\n';
    } else {
      if (tacticalBreakdown.offensiveScheme) {
        prompt += `### Offensive Scheme\n${tacticalBreakdown.offensiveScheme}\n\n`;
      }
      if (tacticalBreakdown.defensiveScheme) {
        prompt += `### Defensive Scheme\n${tacticalBreakdown.defensiveScheme}\n\n`;
      }
      if (tacticalBreakdown.keyAdjustments) {
        prompt += `### Key Adjustments\n${tacticalBreakdown.keyAdjustments}\n\n`;
      }
    }
  }

  // Key moments
  if (keyMoments && keyMoments.length > 0) {
    prompt += '\n## KEY MOMENTS (TIMESTAMPED)\n\n';
    keyMoments.forEach((m, i) => {
      const time = m.timeVideo || m.timestamp || '';
      const event = m.event || m.description || '';
      prompt += `${i + 1}. **[${time}]** - ${event}${m.category ? ` (${m.category})` : ''}\n`;
    });
    prompt += '\n';
  }

  // Player analysis
  if (playerAnalysis && playerAnalysis.length > 0) {
    prompt += '\n## PLAYER ANALYSIS\n\n';
    playerAnalysis.forEach((p) => {
      const name = p.name || p.player || 'Unknown';
      prompt += `### ${name}${p.number ? ` (#${p.number})` : ''}${p.position ? ` - ${p.position}` : ''}\n`;
      if (p.biomechanics) prompt += `- Biomechanics: ${p.biomechanics}\n`;
      if (p.strengths) prompt += `- Strengths: ${p.strengths}\n`;
      if (p.weaknesses) prompt += `- Weaknesses: ${p.weaknesses}\n`;
      if (p.observations) prompt += `- ${p.observations}\n`;
      prompt += '\n';
    });
  }

  prompt += `---

## YOUR ROLE
Answer questions with specific timestamped references, biomechanical insights, tactical analysis, and six-lens grades when relevant. Reference the six lenses (Offensive, Defensive, Performance, Tactical, Biomechanics, Shooting) when discussing player performance.

Be concise but thorough. Reference specific data points and segment timestamps when relevant.`;

  return prompt;
};

// Call Gemini API
const callGeminiAPI = async (userMessage, conversationHistory, gameData) => {
  try {
    const systemPrompt = buildSystemPrompt(gameData);

    // Build messages array for the API
    const messages = [
      { role: 'user', content: systemPrompt },
      { role: 'assistant', content: 'I understand. I have full context on this game. I can provide detailed analysis on player biomechanics, tactical breakdowns, key moments, six-lens grades, and recommendations. What would you like to know?' },
      ...conversationHistory.filter(m => m.role !== 'system').map(m => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content
      })),
      { role: 'user', content: userMessage }
    ];

    const response = await fetch(`${NODE_BACKEND_URL}/api/chat/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages,
        options: {
          model: 'gemini-2.5-flash',
          temperature: 0.7,
          sport: gameData?.gameMetadata?.sport || 'MBB',
          teamId: gameData?.gameMetadata?.teamId || '',
          teamName: gameData?.gameMetadata?.homeTeam || '',
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.content || data.response || data.message || data.text || 'I apologize, but I could not generate a response. Please try again.';
  } catch (error) {
    console.error('Gemini API error:', error);
    // Fallback to local response if API fails
    return `I'm currently unable to connect to the AI service. Based on the game data I have:\n\n${generateFallbackResponse(userMessage)}`;
  }
};

// Fallback response generator when API is unavailable
const generateFallbackResponse = (question) => {
  return 'The AI analysis service is temporarily unavailable. Please try again in a moment.';
};

const VideoChat = ({
  gameData,
  currentMarker,
  translations = {},
  onClose,
  isMinimized = false,
  onToggleMinimize,
}) => {
  const homeTeam = gameData?.gameMetadata?.homeTeam || 'the home team';
  const awayTeam = gameData?.gameMetadata?.awayTeam || 'the away team';
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: `**THE WIRE** - Basketball Intelligence AI\n\nI have **complete access** to game data for **${homeTeam} vs ${awayTeam}**:\n\n- **Tactical Analysis** - Play-by-play segment data\n- **Biomechanics** - Player movement patterns & fatigue indicators\n- **Six-Lens Grades** - Offensive, Defensive, Performance, Tactical, Biomechanics, Shooting\n- **Scouting Context** - Season stats & coaching analysis\n\nWhat would you like to analyze?`,
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const shouldAutoScrollRef = useRef(true); // Only scroll for user-initiated messages

  // Auto-scroll to bottom - only for user-initiated messages, not marker context
  useEffect(() => {
    if (shouldAutoScrollRef.current && messagesContainerRef.current) {
      // Use scrollTop on container to avoid page-level scroll
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when expanded
  useEffect(() => {
    if (!isMinimized) {
      inputRef.current?.focus();
    }
  }, [isMinimized]);

  // Add context message when marker changes - don't auto-scroll for these
  useEffect(() => {
    if (currentMarker) {
      const contextMessage = {
        id: `context-${currentMarker.id}`,
        role: 'system',
        content: `📍 Now viewing: **${currentMarker.event}** at ${currentMarker.timeVideo}\n\n_${currentMarker.analysis}_`,
      };
      // Disable auto-scroll for marker context - don't steal focus from video
      shouldAutoScrollRef.current = false;
      setMessages((prev) => {
        // Don't add duplicate context
        if (prev.some((m) => m.id === contextMessage.id)) return prev;
        return [...prev, contextMessage];
      });
    }
  }, [currentMarker?.id]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessageText = input.trim();
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessageText,
    };

    // Enable auto-scroll for user-initiated messages
    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Call Gemini API with full context
      const response = await callGeminiAPI(userMessageText, messages, gameData);
      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'I encountered an error processing your request. Please try again.',
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Suggested questions (dynamic based on game context)
  const suggestions = [
    `What were the key tactical adjustments in this game?`,
    `Analyze the biomechanics of the key players`,
    `What drove the outcome of this game?`,
    `Which players had the highest six-lens grades?`,
  ];

  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-black/90 backdrop-blur-md border border-white/[0.18] rounded-full shadow-lg hover:bg-black/95 transition-colors"
      >
        <Bot className="w-5 h-5 text-yellow-400" />
        <span className="text-white text-sm font-medium">Ask about the game</span>
        <ChevronUp className="w-4 h-4 text-white/60" />
      </button>
    );
  }

  return (
    <div className="glass-panel rounded-xl overflow-hidden flex flex-col h-full max-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.15] bg-black/40 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-yellow-400/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-yellow-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Game Intelligence</h3>
            <p className="text-white/50 text-xs">Ask about the match</p>
          </div>
        </div>
        {onToggleMinimize && (
          <button
            onClick={onToggleMinimize}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronDown className="w-4 h-4 text-white/60" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.role !== 'user' && (
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                message.role === 'system' ? 'bg-blue-500/20' : 'bg-yellow-400/20'
              }`}>
                {message.role === 'system' ? (
                  <Sparkles className="w-3.5 h-3.5 text-blue-400" />
                ) : (
                  <Bot className="w-3.5 h-3.5 text-yellow-400" />
                )}
              </div>
            )}
            <div
              className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                message.role === 'user'
                  ? 'bg-yellow-400 text-black'
                  : message.role === 'system'
                  ? 'bg-blue-500/10 border border-blue-500/30 text-blue-100'
                  : 'bg-white/[0.08] text-white'
              } prose prose-sm prose-invert max-w-none [&_p]:mb-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_strong]:text-yellow-300 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_code]:bg-white/10 [&_code]:px-1 [&_code]:rounded`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
            />
            {message.role === 'user' && (
              <div className="w-7 h-7 rounded-lg bg-white/[0.12] flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-lg bg-yellow-400/20 flex items-center justify-center">
              <Bot className="w-3.5 h-3.5 text-yellow-400" />
            </div>
            <div className="bg-white/[0.08] px-3 py-2 rounded-xl">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setInput(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-2.5 py-1 text-xs bg-white/[0.06] hover:bg-white/[0.12] text-white/70 hover:text-white rounded-lg transition-colors border border-white/[0.15]"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-white/[0.15]">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={translations.chatPlaceholder || "Ask about the game..."}
            className="flex-1 px-3 py-2 bg-white/[0.06] border border-white/[0.18] rounded-lg text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-yellow-400/50"
            disabled={isTyping}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            className="px-3 py-2 bg-yellow-400 hover:bg-yellow-500 disabled:bg-white/[0.08] disabled:text-white/40 text-black rounded-lg transition-colors"
          >
            {isTyping ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoChat;
